import { useState } from 'react';
import type { MemberDto } from '@/generated/korza-api-client/types.gen';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  canRemoveMember,
  inviteEmailSchema,
  memberNameSchema,
} from '@/lib/team-members';
import { LoaderButton } from '@/components/loader-button';
import { WithTooltip } from '@/components/with-tooltip';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type TeamStatus = 'loading' | 'error' | 'ready';

interface TeamPresenterProps {
  status: TeamStatus;
  members: readonly MemberDto[];
  selfId: string;
  onRetry: () => void;
  /** Resolves to true when the invite was accepted, so the field can be cleared. */
  onInvite: (email: string) => Promise<boolean>;
  isInviting: boolean;
  inviteErrorMessage: string | null;
  /** Resolves to true when the name was saved, so the editor can close. */
  onRenameSelf: (name: string) => Promise<boolean>;
  isRenamingSelf: boolean;
  onRemove: (member: MemberDto) => Promise<void>;
  isRemoving: boolean;
}

export function TeamPresenter({
  status,
  members,
  selfId,
  onRetry,
  onInvite,
  isInviting,
  inviteErrorMessage,
  onRenameSelf,
  isRenamingSelf,
  onRemove,
  isRemoving,
}: TeamPresenterProps) {
  const { LL } = useI18nContext();

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight">
          {LL.team.title()}
        </h1>
        <p className="text-muted-foreground">{LL.team.description()}</p>
      </div>

      <InviteForm
        onInvite={onInvite}
        isInviting={isInviting}
        errorMessage={inviteErrorMessage}
      />

      <Card>
        <CardHeader>
          <h2 className="text-lg font-bold">{LL.team.title()}</h2>
        </CardHeader>
        <CardContent>
          {status === 'loading' && <MembersSkeleton />}
          {status === 'error' && (
            <div className="flex flex-col items-start gap-3 py-4">
              <p className="text-muted-foreground">{LL.team.loadError()}</p>
              <Button variant="outline" onClick={onRetry}>
                {LL.team.retry()}
              </Button>
            </div>
          )}
          {status === 'ready' && members.length === 0 && (
            <p className="py-4 text-muted-foreground">{LL.team.empty()}</p>
          )}
          {status === 'ready' && members.length > 0 && (
            <MembersTable
              members={members}
              selfId={selfId}
              onRenameSelf={onRenameSelf}
              isRenamingSelf={isRenamingSelf}
              onRemove={onRemove}
              isRemoving={isRemoving}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function InviteForm({
  onInvite,
  isInviting,
  errorMessage,
}: {
  onInvite: (email: string) => Promise<boolean>;
  isInviting: boolean;
  errorMessage: string | null;
}) {
  const { LL } = useI18nContext();
  const [email, setEmail] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const parsed = inviteEmailSchema.safeParse(email);
    if (!parsed.success) {
      setIsInvalid(true);
      return;
    }

    setIsInvalid(false);
    // The typed text is kept unless the invite was accepted.
    if (await onInvite(parsed.data)) {
      setEmail('');
    }
  };

  const message = isInvalid ? LL.team.invite.invalidEmail() : errorMessage;

  return (
    <form onSubmit={handleSubmit} className="space-y-2" noValidate>
      <Label htmlFor="team-invite-email">{LL.team.invite.label()}</Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input
          id="team-invite-email"
          type="email"
          autoComplete="off"
          value={email}
          placeholder={LL.team.invite.placeholder()}
          aria-invalid={message !== null}
          disabled={isInviting}
          onChange={(event) => setEmail(event.target.value)}
          className="sm:max-w-sm"
        />
        <LoaderButton
          type="submit"
          isLoading={isInviting}
          aria-label={isInviting ? LL.team.invite.sending() : undefined}
        >
          {LL.team.invite.button()}
        </LoaderButton>
      </div>
      {message !== null && (
        <p role="alert" className="text-sm text-destructive">
          {message}
        </p>
      )}
    </form>
  );
}

function OwnNameCell({
  name,
  onSave,
  isSaving,
}: {
  name: string | null;
  onSave: (name: string) => Promise<boolean>;
  isSaving: boolean;
}) {
  const { LL } = useI18nContext();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [isInvalid, setIsInvalid] = useState(false);

  const startEditing = () => {
    setDraft(name ?? '');
    setIsInvalid(false);
    setIsEditing(true);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const parsed = memberNameSchema.safeParse(draft);
    if (!parsed.success) {
      setIsInvalid(true);
      return;
    }

    setIsInvalid(false);
    // The editor stays open unless the name was saved.
    if (await onSave(parsed.data)) {
      setIsEditing(false);
    }
  };

  if (!isEditing) {
    return (
      <div className="flex items-center gap-2">
        <span>{name ?? '—'}</span>
        <Button variant="ghost" size="sm" onClick={startEditing}>
          {name === null ? LL.team.editName.noName() : LL.team.editName.edit()}
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-1" noValidate>
      <div className="flex items-center gap-2">
        <Input
          autoFocus
          value={draft}
          placeholder={LL.team.editName.namePlaceholder()}
          aria-label={LL.team.editName.edit()}
          aria-invalid={isInvalid}
          disabled={isSaving}
          onChange={(event) => setDraft(event.target.value)}
          className="max-w-xs"
        />
        <LoaderButton
          type="submit"
          size="sm"
          isLoading={isSaving}
          aria-label={isSaving ? LL.team.editName.saving() : undefined}
        >
          {LL.team.editName.save()}
        </LoaderButton>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isSaving}
          onClick={() => setIsEditing(false)}
        >
          {LL.team.editName.cancel()}
        </Button>
      </div>
      {isInvalid && (
        <p role="alert" className="text-sm text-destructive">
          {LL.team.editName.invalid()}
        </p>
      )}
    </form>
  );
}

function MembersSkeleton() {
  return (
    <div className="space-y-3" aria-busy="true">
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
}

function MembersTable({
  members,
  selfId,
  onRenameSelf,
  isRenamingSelf,
  onRemove,
  isRemoving,
}: {
  members: readonly MemberDto[];
  selfId: string;
  onRenameSelf: (name: string) => Promise<boolean>;
  isRenamingSelf: boolean;
  onRemove: (member: MemberDto) => Promise<void>;
  isRemoving: boolean;
}) {
  const { LL } = useI18nContext();
  const [memberToRemove, setMemberToRemove] = useState<MemberDto | null>(null);
  const removable = canRemoveMember(members);

  const confirmRemoval = async (member: MemberDto) => {
    await onRemove(member);
    setMemberToRemove(null);
  };

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{LL.team.nameColumn()}</TableHead>
            <TableHead>{LL.team.emailColumn()}</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {members.map((member) => (
            <TableRow key={member.adminUserId}>
              <TableCell>
                {member.adminUserId === selfId ? (
                  <OwnNameCell
                    name={member.name}
                    onSave={onRenameSelf}
                    isSaving={isRenamingSelf}
                  />
                ) : (
                  (member.name ?? '—')
                )}
              </TableCell>
              <TableCell>
                <span className="mr-2">{member.email}</span>
                {member.adminUserId === selfId && (
                  <Badge variant="secondary">{LL.team.you()}</Badge>
                )}
              </TableCell>
              <TableCell className="text-right">
                <WithTooltip
                  tooltip={LL.team.remove.lastMemberTooltip()}
                  disabled={removable}
                >
                  <span className="inline-block">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!removable}
                      onClick={() => setMemberToRemove(member)}
                    >
                      {LL.team.remove.button()}
                    </Button>
                  </span>
                </WithTooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <AlertDialog
        open={memberToRemove !== null}
        onOpenChange={(open) => {
          if (!open && !isRemoving) {
            setMemberToRemove(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{LL.team.remove.dialogTitle()}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>
                  {LL.team.remove.dialogDescription({
                    email: memberToRemove?.email ?? '',
                  })}
                </p>
                {memberToRemove?.adminUserId === selfId && (
                  <p>{LL.team.remove.dialogSelfWarning()}</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>
              {LL.team.remove.cancel()}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={isRemoving}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(event) => {
                event.preventDefault();
                if (memberToRemove) {
                  void confirmRemoval(memberToRemove);
                }
              }}
            >
              {LL.team.remove.confirm()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
